<template>
    <div class=" col-12 overflow-hidden print:overflow-visible">
        <div class="pl-1 d-inline justify-content-center w-25">
            <router-link
                to="/projects"
                class="justify-content-center pt-1 btn-md mr-3 btn btn-secondary"
            >
                &laquo; Projects
            </router-link>
        </div>
        <page-header :title="title" :items="items" class="pl-1 pt-3" />

        <project-details />

        <br />

        <b-card
            bg-variant="light"
            header-class="card-header  p-3 h5"
            header-text-variant="black"
            header-tag="header"
        >
            <div slot="header">
                <div class="d-flex align-items-center">
                    <div>
                        <i class="mdi mdi-account-circle " />
                        New Calculations Request
                    </div>
                    <div class="pl-3">
                        <b-button
                            class="btn btn-success waves-effect waves-light h-10"
                            style="font-weight:500"
                            data-animation="fadein"
                            data-overlaycolor="#38414a"
                            @click="onOpenNewCalcRequest"
                        >
                            <i class="fa fa-plus-square" aria-hidden="true" />
                            Create
                        </b-button>
                    </div>
                </div>
            </div>
            <new-calculations-request
                :project-data="project"
                :modal-show="showNewCalcReqModal"
                @close="onCloseNewCalcRequest"
                @getRequests="getRequests"
            />
            <br />
            <awesome-table
                :options="options"
                :pagination="pagination"
                :labels="labels"
                :items="calculationsRequests"
                :get-row-class="getRowClass"
                @rowClick="showCalculationsRequestInfoModal"
                @paginationChange="onPaginationChange"
            >
                <template #actions="{ item }">
                    <div class="flex flex-row flex-wrap">
                        <i
                            class="far fa-trash-alt action-icon p-2 text-primary cursor-pointer"
                            @click.stop.prevent="showDeleteModal(item.id)"
                        />
                        <i
                            class="fa fa-file-download action-icon p-2 text-primary cursor-pointer"
                            :class="{
                                'fa-disabled ': item.status !== 'Completed'
                            }"
                            @click.stop.prevent="downloadOutput(item.id)"
                        />
                        <i
                            class="fas fa-comment-alt p-2 text-primary cursor-pointer"
                            @click.stop.prevent="openEditNotesModal(item)"
                        />
                        <i
                            v-if="isSuperAdmin"
                            class="fas fa-cogs action-icon p-2 text-primary cursor-pointer"
                            :class="{
                                'fa-disabled ': !item.errorDescription
                            }"
                            @click.stop.prevent="relaunchRequest(item.id)"
                        />
                    </div>
                </template>

                <template #results="{ item }">
                    <div class="flex flex-row flex-wrap">
                        <i
                            v-if="
                                item.allQueueJobsData &&
                                    item.allQueueJobsData.some(
                                        item => item.pdfReportPublisher
                                    )
                            "
                            class="fas fa-table action-icon p-2 text-primary cursor-pointer"
                            :class="{
                                'fa-disabled ': item.status !== 'Completed'
                            }"
                            @click="selectedRequest.id = item.id"
                            @click.stop.prevent="showResultsModal(item)"
                        />

                        <i
                            v-if="hasHcalculationsPdf(item)"
                            class="fas fa-h-square  action-icon p-2 text-primary cursor-pointer"
                            :class="{
                                'fa-disabled ': item.status !== 'Completed'
                            }"
                            @click="selectedRequest.id = item.id"
                            @click.stop.prevent="showHCalculationsModal(item)"
                        />
                    </div>
                </template>

                <template #avgSavingPercentage="{ item }">
                    <div class="flex flex-row flex-wrap">
                        {{
                            item.avgSavingPercentage
                                ? `${item.avgSavingPercentage.toFixed()}%`
                                : '-'
                        }}
                    </div>
                </template>

                <template #notes="{ item }">
                    <div>
                        <span
                            v-if="notesLengthStatement(item)"
                            :key="`notes_${item.id}`"
                            v-b-tooltip.hover.left.html="showNotes(item)"
                            class="large-tooltip black d-inline-block"
                        >
                            {{ shortenNotes(item.notes) }}
                        </span>
                        <span v-else>
                            {{ item.notes }}
                        </span>
                    </div>
                </template>

                <template #status="{ item }">
                    <div>
                        <span
                            :key="`status_${item.id}`"
                            v-b-tooltip.hover.top.html="
                                item.status && item.status.includes('Error')
                                    ? showErrorDescription(item)
                                    : ''
                            "
                            class="large-tooltip black d-inline-block"
                        >
                            <div
                                v-if="item.status"
                                class="flex flex-row flex-wrap"
                            >
                                <span>
                                    {{ item.status }}
                                </span>
                            </div>
                        </span>
                    </div>
                </template>
            </awesome-table>

            <calculation-request-results
                :modal-id="selectedRequest.id"
                :calculation-request-results="buildingRatingResults"
                :is-open="isCalcReqResultsModalOpen"
                @close="closeCalcReqResultsModal"
            />

            <calculation-request-info
                v-if="isSuperAdmin && isOpenDebugInfoModal"
                :show-modal="isOpenDebugInfoModal"
                :calculation-request-debug-info="selectedRequest"
                @hide="hideCalculationsRequestInfoModal"
            />

            <delete-modal
                :id="`confirm-delete-modal-${idToDelete}`"
                :is-open="isDeleteModalOpen"
                :title="
                    `Are you sure you want to delete this rating calculations?`
                "
                @agree="removeCalculationsRequest"
                @disagree="onCloseDeleteModal"
            />

            <edit-notes-modal
                :editing-note="notesToEdit"
                :calculations-request-id="editingNoteCalculationsRequestId"
                :modal-id="editNotesModalId"
                @updated="onNotesUpdated"
            />

            <h-calculations-modal
                :modal-id="selectedRequest.id"
                :is-open="isHCalculationsModalOpen"
                :path="hCalculationsPdfPath"
                @close="closeHCalculationsModal"
            />
        </b-card>
    </div>
</template>

<script>
import * as dayjs from 'dayjs';

import { mapActions, mapGetters } from 'vuex';
import HCalculationsModal from '@/components/h-calculations/HCalculationsModal.vue';
import CalculationRequestInfo from '@/components/debug/CalculationRequestInfo.vue';
import CalculationRequestResults from '@/components/calculations-requests/CalculationRequestResults.vue';
import NewCalculationsRequest from '@/components/calculations-requests/NewCalculationsRequest.vue';
import EditNotesModal from '@/components/calculations-requests/EditNotesModal.vue';
import DeleteModal from '@/components/elements/DeleteModal';
import ProjectDetails from '@/components/projects/Details';
import { fileUrl } from '@/config/index';

export default {
    page: {
        title: 'Greener - Project Details'
    },

    components: {
        HCalculationsModal,
        NewCalculationsRequest,
        DeleteModal,
        ProjectDetails,
        EditNotesModal,
        CalculationRequestInfo,
        CalculationRequestResults
    },

    data() {
        return {
            pagination: {
                currentPage: 1,
                perPage: 10,
                total: 0,
                search: this.$route.params.id
            },
            lengthOfShrotenedNote: 70,
            idToDelete: null,
            buildingRatingResults: [],
            notesToEdit: '',
            hCalculationsPdfPath: '',
            editingNoteCalculationsRequestId: null,
            selectedRequest: {},
            calculationsRequests: [],
            isCalcReqResultsModalOpen: false,
            isHCalculationsModalOpen: false,
            isDeleteModalOpen: false,
            isOpenDebugInfoModal: false,
            title: 'Calculation Requests',
            showNewCalcReqModal: false,
            project: null,
            projectId: this.$route.params.id,
            options: {
                addButton: false,

                searchInput: false,

                clickableRows: false
            },
            labels: [
                {
                    value: 'createdAt',
                    width: '10%',
                    filter: 'formatDate',
                    type: 'date'
                },
                { value: 'processingFileName', width: '15%' },
                { value: 'rank', width: '5%' },
                {
                    value: 'avgSavingPercentage',
                    width: '5%'
                },
                { value: 'results', width: '10%' },
                { value: 'status', width: '15%' },
                {
                    value: 'notes',
                    width: '30%',
                    filterable: false
                },
                {
                    value: 'actions',
                    width: '5%',
                    filterable: false
                }
            ],
            items: [
                {
                    text: 'Greener',
                    to: '/'
                },
                {
                    text: 'Project Details',
                    active: true
                }
            ],
            weatherFiles: [],
            climates: [
                { key: 'A', label: 'Climate A' },
                { key: 'B', label: 'Climate B' },
                { key: 'C', label: 'Climate C' },
                { key: 'D', label: 'Climate D' }
            ],
            selectedWeather: null,
            climate: null
        };
    },

    computed: {
        ...mapGetters({
            isSuperAdmin: 'auth/isSuperAdmin',
            getProjectById: 'projects/getById',
            getCalculationsRequestsByProjectId:
                'calculationsRequests/getCalculationsRequestsByProjectId'
        }),

        editNotesModalId() {
            return (
                'editNotesModal' +
                Math.random()
                    .toString(36)
                    .substring(2, 15)
                    .match(/[a-zA-Z]+/g)
                    .join('')
            );
        },

        hasHcalculationsPdf() {
            return item => {
                const pdfPublisherData = this.getPdfReportPublisherData(item);

                if (
                    pdfPublisherData &&
                    pdfPublisherData.some(data => Array.isArray(data))
                ) {
                    const attachmenets = pdfPublisherData.find(data =>
                        Array.isArray(data)
                    );

                    const hCalculationsPdf = attachmenets.find(
                        attachment =>
                            attachment.filename === `H-Calculations.pdf`
                    );

                    return !!hCalculationsPdf;
                }

                return false;
            };
        }
    },

    async created() {
        await this.fetchProjects();
        await this.getRequests();
        this.project = this.getProjectById(this.projectId);
        this.title = `Calculation Requests for ${this.project.name}`;
    },

    methods: {
        ...mapActions({
            fetchProjects: 'projects/fetchProjects',
            fetchRequests: 'calculationsRequests/index',
            deleteRequest: 'calculationsRequests/destroy',
            getProject: 'projects/show',
            getFile: 'files/show',
            relaunchRequest: 'relaunchRequest/store'
        }),

        showCalculationsRequestInfoModal(item) {
            if (this.isSuperAdmin) {
                const { currentPublisher, allQueueJobsData } = item;
                this.selectedRequest = { currentPublisher, allQueueJobsData };
                this.isOpenDebugInfoModal = true;
            }
        },

        hideCalculationsRequestInfoModal() {
            this.isOpenDebugInfoModal = false;
            this.selectedRequest = {};
        },

        openEditNotesModal(calculationsRequest) {
            this.notesToEdit = calculationsRequest.notes;
            this.editingNoteCalculationsRequestId = calculationsRequest.id;
            this.$bvModal.show(this.editNotesModalId);
        },

        shortenNotes(notes) {
            return notes && notes.length > this.lengthOfShrotenedNote
                ? notes.substring(0, this.lengthOfShrotenedNote) + '..'
                : notes;
        },

        showNotes(item) {
            return `<div class="bubble">${item.notes}</div>`;
        },

        showErrorDescription(item) {
            return `<span class="large-tooltip black-bubble">${item.errorDescription}</span>`;
        },

        async onNotesUpdated() {
            this.calculationsRequests = this.getCalculationsRequestsByProjectId(
                this.projectId
            );
        },

        showDeleteModal(calculationsRequestId) {
            this.idToDelete = calculationsRequestId;
            this.isDeleteModalOpen = true;
        },

        notesLengthStatement(item) {
            return item.notes && item.notes.length > this.lengthOfShrotenedNote;
        },

        onCloseDeleteModal() {
            this.idToDelete = null;
            this.isDeleteModalOpen = false;
        },

        getRowClass(item) {
            const isAdminClass = this.isSuperAdmin ? 'clickable-row' : '';
            let isEqualF = '';

            const data = this.getPdfReportPublisherData(item);

            if (data) {
                isEqualF = data.some(result => result.rank === 'F')
                    ? 'rank-f'
                    : '';
            }

            return `${isAdminClass} ${isEqualF}`;
        },

        getPdfReportPublisherData(item) {
            if (
                item.allQueueJobsData &&
                item.allQueueJobsData.some(item => item.pdfReportPublisher)
            ) {
                const { pdfReportPublisher } = item.allQueueJobsData.find(
                    item => item.pdfReportPublisher
                );

                return pdfReportPublisher;
            }
        },

        showResultsModal(item) {
            const data = this.getPdfReportPublisherData(item);

            if (data) {
                this.buildingRatingResults = data
                    .filter(prop => !Array.isArray(prop))
                    .map(({ id, rank, floorNumber, unitNumber }) => ({
                        id,
                        rank,
                        floorNumber,
                        unitNumber
                    }));
            }

            this.selectedRequest.id = item.id;

            this.isCalcReqResultsModalOpen = true;
        },

        showHCalculationsModal({ id }) {
            this.selectedRequest.id = id;

            const calculationsRequest = this.calculationsRequests.find(
                ({ id }) => id === this.selectedRequest.id
            );

            const {
                path: hCalculationsPdfPath
            } = this.getPdfReportPublisherData(calculationsRequest)
                .find(data => Array.isArray(data))
                .find(
                    attachment => attachment.filename === 'H-Calculations.pdf'
                );

            const pdfPath = `${fileUrl}/${hCalculationsPdfPath
                .split('public')
                .pop()}`;

            this.hCalculationsPdfPath = `../lib/pdfjs-2.8.335-legacy-dist/web/viewer.html?file=${pdfPath}`;

            this.isHCalculationsModalOpen = true;
        },

        closeCalcReqResultsModal() {
            this.isCalcReqResultsModalOpen = false;
        },

        closeHCalculationsModal() {
            this.isHCalculationsModalOpen = false;
        },

        async getRequests() {
            try {
                const { rows, count } = await this.fetchRequests(
                    this.pagination
                );

                this.calculationsRequests = rows;

                this.pagination.pages = Math.ceil(
                    count / this.pagination.perPage
                );
                this.pagination.total = count;
            } catch (error) {
                console.error(error);
            }
        },

        async downloadOutput(requestId) {
            const file = await this.getFile({ id: requestId });
            const zipUrl = `${fileUrl}/output/climate_${this.project.climate}/${requestId}/${file.filename}`;
            const fileLink = document.createElement('a');
            fileLink.href = zipUrl;
            fileLink.download = `Rating_Output_${dayjs()}.zip`;

            await fileLink.click();
        },

        async removeCalculationsRequest() {
            try {
                await this.deleteRequest(this.idToDelete);

                this.idToDelete = null;

                this.$bvToast.toast('calculationsRequest has been deleted.', {
                    title: 'Success!',
                    variant: 'success',
                    solid: true
                });

                await this.getRequests();

                this.isDeleteModalOpen = false;
            } catch (error) {
                console.error(error);

                const message =
                    error.response &&
                    error.response.data &&
                    error.response.data.error
                        ? error.response.data.error
                        : 'Something went wrong!';

                this.$bvToast.toast(message, {
                    title: 'Error!',
                    variant: 'danger',
                    solid: true
                });
            }
        },

        onPaginationChange(pagination) {
            this.pagination = {
                ...pagination
            };

            this.getRequests();
        },

        async onOpenNewCalcRequest() {
            await this.fetchProjects();

            this.showNewCalcReqModal = true;
        },

        async onCloseNewCalcRequest() {
            await this.getRequests();

            this.showNewCalcReqModal = false;
        }
    }
};
</script>

<style scoped>
.fa-disabled {
    opacity: 0.6;
    cursor: not-allowed;
    color: gray !important;
}
</style>
